/**
 * Device fingerprint generator.
 * Creates a stable identifier from browser characteristics + persistent storage.
 * Used for: single-session enforcement, trial abuse prevention, device tracking.
 */

const STORAGE_KEY = "resumeai_did";

/**
 * Generate a hash from a string using djb2 algorithm.
 */
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(8, "0");
}

/**
 * Generate a browser-characteristics fingerprint (not persistent on its own).
 */
function getBrowserFingerprint() {
  const components = [
    navigator.userAgent || "",
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    navigator.language || "",
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency?.toString() || "0",
    navigator.platform || "",
    typeof navigator.maxTouchPoints !== "undefined"
      ? navigator.maxTouchPoints.toString()
      : "0",
  ];
  return hashString(components.join("|"));
}

/**
 * Get or create a persistent device ID.
 * Uses localStorage as primary, falls back to cookie, then generates new.
 * Combines persistent ID with browser fingerprint for stability.
 */
export function generateDeviceFingerprint() {
  const browserFP = getBrowserFingerprint();

  // Try localStorage first
  let persistentId = null;
  try {
    persistentId = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }

  // Try cookie fallback
  if (!persistentId) {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${STORAGE_KEY}=([^;]*)`)
    );
    if (match) {
      persistentId = decodeURIComponent(match[1]);
      // Sync back to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, persistentId);
      } catch {
        /* ignore */
      }
    }
  }

  // Generate new ID if none exists
  if (!persistentId) {
    const random = Math.random().toString(36).substring(2, 10);
    const ts = Date.now().toString(36);
    persistentId = `${browserFP}-${random}-${ts}`;

    // Store in both localStorage and cookie
    try {
      localStorage.setItem(STORAGE_KEY, persistentId);
    } catch {
      /* ignore */
    }
    try {
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${STORAGE_KEY}=${encodeURIComponent(persistentId)}; expires=${expires}; path=/; SameSite=Lax`;
    } catch {
      /* ignore */
    }
  }

  // Combine persistent ID + browser fingerprint for final device ID
  return hashString(`${persistentId}|${browserFP}`);
}

/**
 * Parse user-agent into readable browser + OS strings.
 */
export function getDeviceInfo() {
  const ua = navigator.userAgent || "";
  let browser = "Unknown Browser";
  let os = "Unknown OS";

  // Browser detection
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("OPR/") || ua.includes("Opera")) browser = "Opera";
  else if (ua.includes("Chrome/") && !ua.includes("Edg/")) browser = "Chrome";
  else if (ua.includes("Safari/") && !ua.includes("Chrome/")) browser = "Safari";
  else if (ua.includes("Firefox/")) browser = "Firefox";

  // OS detection
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux") && !ua.includes("Android")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  else if (ua.includes("CrOS")) os = "ChromeOS";

  return { browser, os, userAgent: ua };
}
